import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Newspaper } from 'lucide-react';
import { agentsApi } from '@/lib/agents';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';

/**
 * Proactive "this week" brief on the workspace home. One click asks the model
 * to summarise what's new, what needs attention, and a suggested focus — the
 * morning-digest pattern, so the workspace tells the user where to look.
 */
export function WeeklyDigest({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const gen = useMutation({ mutationFn: () => agentsApi.digest(wsId) });
  const d = gen.data;

  return (
    <section className="card-paper p-5 mb-7">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-xl bg-citation-50 text-citation-700 inline-flex items-center justify-center">
            <Newspaper size={16} />
          </span>
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-citation-600 font-semibold">
              {t('digest.eyebrow', { defaultValue: 'This week' })}
            </div>
            <div className="text-[14.5px] font-medium text-ink-900">
              {t('digest.title', { defaultValue: 'Your workspace brief' })}
            </div>
          </div>
        </div>
        <button
          onClick={() => gen.mutate()}
          disabled={gen.isPending}
          className="btn-ghost px-3.5 py-2 text-sm shrink-0 disabled:opacity-50"
        >
          {gen.isPending
            ? t('digest.generating', { defaultValue: 'Writing…' })
            : d
              ? t('digest.regenerate', { defaultValue: 'Refresh' })
              : t('digest.generate', { defaultValue: 'Generate brief' })}
        </button>
      </div>

      {gen.isError && (
        <p className="mt-3 text-[13px] text-warn-600">
          {t('common.action_failed', { defaultValue: 'Something went wrong. Please try again.' })}
        </p>
      )}

      {d && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2 mb-3 text-[12px]">
            <Chip label={t('digest.new_docs', { defaultValue: '{{n}} new docs', n: d.stats.newDocCount })} />
            {d.stats.pendingApprovals > 0 && (
              <Chip warn label={t('digest.pending', { defaultValue: '{{n}} approvals waiting', n: d.stats.pendingApprovals })} />
            )}
            {d.stats.failedDocs > 0 && (
              <Chip warn label={t('digest.failed', { defaultValue: '{{n}} failed to index', n: d.stats.failedDocs })} />
            )}
            <Chip label={t('digest.answers', { defaultValue: '{{n}} answers (7d)', n: d.stats.quality.totalAnswers })} />
          </div>
          {d.brief ? (
            <CitedMarkdown content={d.brief} wsId={wsId} />
          ) : (
            <p className="text-[13px] text-ink-500">{t('digest.empty', { defaultValue: 'Not enough recent activity to summarise yet.' })}</p>
          )}
        </div>
      )}
    </section>
  );
}

function Chip({ label, warn }: { label: string; warn?: boolean }) {
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 border font-medium ${
        warn ? 'bg-warn-500/10 text-warn-600 border-warn-500/20' : 'bg-paper-100 text-ink-600 border-ink-200'
      }`}
    >
      {label}
    </span>
  );
}
